import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import TaxInvoicePrintLayout from '../../shared/TaxInvoice/TaxInvoicePrintLayout';
import TaxInvoicePrintModal from '../../shared/TaxInvoice/TaxInvoicePrintModal';
import { normalizeTaxInvoiceData } from '../../shared/TaxInvoice/normalizeTaxInvoiceData';
import { formatDate, formatCurrency, getText } from '../../shared/TaxInvoice/taxInvoiceUtils';

const CashMemoPreview = ({ data, isLoading }) => {
    const [searchParams] = useSearchParams();
    const preview = searchParams.get('preview');
    const [showPrintModal, setShowPrintModal] = useState(false);

    useEffect(() => {
        if (!data) return;
        if (preview) setShowPrintModal(true);
    }, [preview, data]);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="text-center p-4">
                <h5>No data available</h5>
            </div>
        );
    }

    const handlePrint = () => {
        setShowPrintModal(true);
    };

    const handleClosePrintModal = () => {
        setShowPrintModal(false);
    };

    return (
        <div className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Cash Memo Details</h5>
                </div>
                <div className="d-flex justify-content-end">
                    <button 
                        className="btn btn-primary btn-sm no-print"
                        onClick={handlePrint}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                        <i className="fas fa-print"></i>
                        Print Preview
                    </button>
                </div>
            </div>

            <div className="flex-grow-1">
                <div className="row">
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Invoice Number:</label>
                            <div className="form-control-plaintext">{data.invoice_number || 'N/A'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Invoice Date:</label>
                            <div className="form-control-plaintext">{formatDate(data.invoice_date)}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Customer:</label>
                            <div className="form-control-plaintext">{data.customer?.label || data.customer_name || 'N/A'}</div>
                        </div>
                    </div>
                </div>

                <div className="table-responsive mt-4">
                    <table className="table table-bordered">
                        <thead>
                            <tr>
                                <th className="text-nowrap p-3">Item Code</th>
                                <th className="text-nowrap p-3">Item Name</th>
                                <th className="text-nowrap p-3">Unit</th>
                                <th className="text-nowrap p-3">Qty</th>
                                <th className="text-nowrap p-3">Unit Price</th>
                                <th className="text-nowrap p-3">Discount %</th>
                                <th className="text-nowrap p-3">Line Amount</th>
                                <th className="text-nowrap p-3">VAT %</th>
                                <th className="text-nowrap p-3">VAT Amt</th>
                                <th className="text-nowrap p-3">Amt Incl VAT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items && data.items.length > 0 ? (
                                data.items.map((item, index) => (
                                    <tr key={index}>
                                        <td className="text-nowrap p-3 align-middle">{item.item?.item_code || 'N/A'}</td>
                                        <td className="text-nowrap p-3 align-middle">{item.item_description || 'N/A'}</td>
                                        <td className="p-3 align-middle">{item.unit || 'N/A'}</td>
                                        <td className="p-3 align-middle">{item.quantity || '0'}</td>
                                        <td className="p-3 align-middle">{formatCurrency(item.unit_price)}</td>
                                        <td className="p-3 align-middle">{item.discount_percentage || '0.00'}%</td>
                                        <td className="p-3 align-middle">{formatCurrency(item.line_amount)}</td>
                                        <td className="p-3 align-middle">{item.vat_percentage || '0.00'}%</td>
                                        <td className="p-3 align-middle">{formatCurrency(item.vat_amount)}</td>
                                        <td className="p-3 align-middle">{formatCurrency(item.amount_incl_vat)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="10" className="text-center">No items added</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="row mt-4 d-flex justify-content-end">
                    <div className="col-md-4">
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Gross Total:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.gross_total)}</div>
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Discount %:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{data.discount_percentage || '0.00'}%</div>
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Discount Amt:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.discount_amount)}</div>
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Sub Total:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.sub_total)}</div>
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">VAT Amt:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.vat_amount)}</div>
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Net Total:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.net_total)}</div>
                            </div>
                        </div>

                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Advance:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.advance_payment)}</div>
                            </div>
                        </div>
                        
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Payment:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.balance - data.advance_payment)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <TaxInvoicePrintModal
                open={showPrintModal}
                onClose={handleClosePrintModal}
                title="Cash Memo Print"
                documentType="cash_memo"
                documentId={data?.id}
            >
                <TaxInvoicePrintLayout data={normalizeTaxInvoiceData(data, 'cash_memo')} />
            </TaxInvoicePrintModal>
        </div>
    );
};

export default CashMemoPreview;
